import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, ElementRef, QueryList, ViewChildren, ViewContainerRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ShippingPolicyComponent } from 'src/app/components/footer/components/shipping-policy/shipping-policy.component';
import { LoadingState } from 'src/app/core/constants/observable-state.interface';
import { Announcement, AnnouncementBarPage, ProductBadge } from 'src/app/core/interfaces/store-configuration.interface';
import { AnnouncementsService } from 'src/app/core/services/announcements/announcements.service';
import { CartRepositoryService } from 'src/app/core/services/cart/cart-repository.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { FullScreenService } from 'src/app/shared/full-screen/services/full-screen.service';
import { Image, OtherOffers, Product, ProductDealCountdown, ProductStateManager, createDefaultProduct } from '../../models/product.model';
import { DealCountdownService, RemainingTime } from '../../services/deal-countdown.service';
import { PriceService } from '../../services/price.service';
import { ProductAttributeSelectionsService } from '../../services/product-attribute-selections.service';
import { ProductsRepositoryService } from '../../services/products-repository.service';
import { ScrollAbleSectionIds, ScrollAbleSectionViewLabels } from '../explore/explore.component';
import { ImageCarouselFullScreenComponent } from '../image-carousel-full-screen/image-carousel-full-screen.component';
import { MobileImageCarouselFullScreenComponent } from '../mobile-image-carousel-full-screen/mobile-image-carousel-full-screen.component';

import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { MobileFullScreenService } from 'src/app/shared/full-screen/services/mobile-full-screen.service';
import { CartData } from '../../../core/interfaces/cart.interface';
// Component imports
import { AnnouncementBarComponent } from 'src/app/components/announcement-bar/announcement-bar.component';
import { CashPointSvgComponent } from 'src/app/components/cash-point-svg/cash-point-svg.component';
import { ImageViewerComponent } from 'src/app/components/image-viewer/image-viewer.component';
import { ChatGptComponent } from '../chat-gpt/chat-gpt.component';
import { DescriptionComponent } from '../description/description.component';
import { DimensionAndWeightComponent } from '../dimension-and-weight/dimension-and-weight.component';
import { ProductAvailableOffersComponent } from '../dynamic-product-details/product-details-template/product-primary-details/product-avilable-offers/product-available-offers.component';
import { DynamicVariantsComponent } from '../dynamic-variants/dynamic-variants.component';
import { ExploreComponent } from '../explore/explore.component';
import { HighlightsComponent } from '../highlights/highlights.component';
import { ProductBadgeComponent } from '../product-badge/product-badge.component';
import { ShortDescriptionComponent } from '../short-description/short-description.component';
// Pipe imports
import { LoadingStatesWrapperComponent } from 'src/app/components/loading-states-wrapper/loading-states-wrapper.component';
import { RelatedProductsWrapperComponent } from 'src/app/components/related-products-carousel/components/related-products-wrapper/related-products-wrapper.component';
import { TagComponent } from "src/app/components/tag/tag/tag.component";
import { CashpointsPipe } from 'src/app/shared/pipes/cashpoints.pipe';
import { IndianCurrencyPipe } from 'src/app/shared/pipes/indian-currency.pipe';
import { PercentageDiscountPipe } from 'src/app/shared/pipes/percentage-discount.pipe';
import { DownloadsComponent } from '../downloads/downloads.component';
import { FeaturesComponent } from '../features/features.component';
import { ImagesComponent } from '../images/images.component';
import { ProductCartConfigurationComponent } from '../product-cart-configuration/product-cart-configuration.component';
import { ProductsInBoxComponent } from '../products-in-box/products-in-box.component';
import { SpecificationsComponent } from "../specifications/specifications.component";
import { VideosComponent } from '../videos/videos.component';

@Component({
  standalone: true,
  selector: 'webstore-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductAttributeSelectionsService, DealCountdownService],
  imports: [
    CommonModule,
    RouterModule,
    CashpointsPipe,
    IndianCurrencyPipe,
    PercentageDiscountPipe,
    // Components from modules
    AnnouncementBarComponent,
    ImageViewerComponent,
    ShortDescriptionComponent,
    ProductAvailableOffersComponent,
    DynamicVariantsComponent,
    ProductBadgeComponent,
    ChatGptComponent,
    DimensionAndWeightComponent,
    HighlightsComponent,
    ExploreComponent,
    DescriptionComponent,
    CashPointSvgComponent,
    TagComponent,

    RelatedProductsWrapperComponent,
    LoadingStatesWrapperComponent,
    ProductsInBoxComponent,
    FeaturesComponent,
    DownloadsComponent,
    ImagesComponent,
    VideosComponent,
    ProductCartConfigurationComponent,
    SpecificationsComponent
  ]
})
export class ProductDetailComponent {
  priceService = inject(PriceService);
  cartRepositoryService = inject(CartRepositoryService);
  notificationService = inject(NotificationService);
  private productAttributeSelectionsService = inject(ProductAttributeSelectionsService);
  private platformService = inject(PlatformService);
  announcementsService = inject(AnnouncementsService);
  private readonly deviceDetectionService = inject(DeviceDetectionService);
  overlayService = inject(OverlayService);
  storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private fullScreenService = inject(FullScreenService);
  private mobileFullScreenService = inject(MobileFullScreenService);
  private viewContainerRef = inject(ViewContainerRef);
  private dealContDown = inject(DealCountdownService);
  private document = inject<Document>(DOCUMENT);
  private productsRepositoryService = inject(ProductsRepositoryService);

  // Device detection computed signals
  readonly isDesktop = computed(() => this.deviceDetectionService.isDesktop);
  readonly isMobile = computed(() => this.deviceDetectionService.isMobile);
  readonly isTablet = computed(() => this.deviceDetectionService.isTablet);
  readonly deviceType = computed(() => this.deviceDetectionService.deviceType);

  // Signal-based properties
  private _sellingPrice = signal<number>(0);
  private _percentSavings = signal<number>(0);
  private _actualPrice = signal<number>(0);
  private _availableOffers = signal<OtherOffers[]>([]);
  private _announcement = signal<Announcement | null>(null);
  private _badges = signal<ProductBadge[]>([]);
  private _remainingTime = signal<RemainingTime | null>(null);
  private _pageMask = signal<boolean>(false);
  private _storeConfiguration = signal<any>(null);
  private _showGptWidgets = signal<boolean>(false);

  // Computed signals
  readonly sellingPrice = this._sellingPrice.asReadonly();
  readonly percentSavings = this._percentSavings.asReadonly();
  readonly actualPrice = this._actualPrice.asReadonly();
  readonly availableOffers = this._availableOffers.asReadonly();
  readonly announcement = this._announcement.asReadonly();
  readonly badges = this._badges.asReadonly();
  readonly remainingTime = this._remainingTime.asReadonly();
  readonly pageMask = this._pageMask.asReadonly();
  readonly storeConfiguration = this._storeConfiguration.asReadonly();
  readonly showGptWidgets = this._showGptWidgets.asReadonly();

  @ViewChildren('scrollAble') scrollAbleSections!: QueryList<ElementRef>;



  readonly productInput = input<Product>(createDefaultProduct());

  private _product = signal<Product>(createDefaultProduct());
  readonly productSignal = this._product.asReadonly();

  IMAGE_URLS = IMAGE_URLS;
  ScrollAbleSectionIds = ScrollAbleSectionIds;
  ScrollAbleSectionViewLabels = ScrollAbleSectionViewLabels;
  private _currentAttributeSelections = signal<FormGroup>(new FormGroup({}));
  readonly currentAttributeSelections = this._currentAttributeSelections.asReadonly();
  isBrowser = false;

  // Computed signals for derived state
  readonly validDescription = computed(() => {
    const product = this._product();
    const div = this.document.createElement('div');
    div.innerHTML = product?.one_line_description ?? "";
    const isValid = div?.innerText?.length;
    div.remove();
    return isValid;
  });

  readonly showProductDetails = computed(() => {
    const product = this._product();
    return product?.dimensions || product?.weight || product?.unit || product?.manufacturer || product?.country_of_origin;
  });

  readonly productHighlights = computed(() => {
    const product = this._product();
    return product?.highlights || [];
  });

  readonly productsInABox = computed(() => {
    const product = this._product();
    const productsInBox = product?.products_in_box || {};
    return productsInBox;
  });

  readonly productAssets = computed(() => {
    const product = this._product();
    return product?.assets || {};
  });

  readonly isMobileValue = computed(() => {
    return this.deviceDetectionService.isMobile();
  });

  readonly showEstimatedDeliveryValue = computed(() => {
    return this.storeConfigurationRepositoryService.showEstimatedDelivery() ?? false;
  });

  readonly productMrp = computed(() => {
    const product = this._product();
    return product?.mrp || 0;
  });

  readonly productPriceList = computed(() => {
    const product = this._product();
    return product?.price_list || {};
  });

  // Additional computed signals for all sections with proper loading state management
  readonly productAccessories = computed(() => {
    const product = this._product();
    const accessories = product?.accessories || [];
    return {
      loadingState: LoadingState.Loaded,
      response: accessories
    };
  });

  readonly productFrequentlyBoughtTogether = computed(() => {
    const product = this._product();
    const frequentlyBought = product?.frequently_bought_together || [];
    return {
      loadingState: LoadingState.Loaded,
      response: frequentlyBought
    };
  });

  readonly productSimilarProducts = computed(() => {
    const product = this._product();
    const similarProducts = product?.similar_products || [];
    return {
      loadingState: LoadingState.Loaded,
      response: similarProducts
    };
  });

  readonly productFeatures = computed(() => {
    const features = this.productStateManager.features();
    return features;
  });

  readonly productFeaturesState = computed(() => {
    const features = this.productStateManager.features();
    const loading = this.productStateManager.featuresLoading();
    const error = this.productStateManager.featuresError();
    return {
      response: features,
      loadingState: loading ? LoadingState.Loading : (error ? LoadingState.Error : LoadingState.Loaded)
    };
  });

  readonly productSponsoredProducts = computed(() => {
    const product = this._product();
    const sponsoredProducts = product?.sponsored_products || [];
    return {
      loadingState: LoadingState.Loaded,
      response: sponsoredProducts
    };
  });

  readonly productDownloads = computed(() => {
    const downloads = this.productStateManager.downloads();
    return downloads || {
      catalog: [],
      howToGuide: [],
      price_list: [],
      user_manuals: []
    };
  });

  readonly productDownloadsState = computed(() => {
    const downloads = this.productStateManager.downloads();
    const loading = this.productStateManager.downloadsLoading();
    const error = this.productStateManager.downloadsError();
    return {
      response: downloads || {
        catalog: [],
        howToGuide: [],
        price_list: [],
        user_manuals: []
      },
      loadingState: loading ? LoadingState.Loading : (error ? LoadingState.Error : LoadingState.Loaded)
    };
  });

  readonly productAssetsShared = computed(() => {
    const assets = this.productStateManager.assets();
    return assets;
  });

  readonly productAssetsState = computed(() => {
    const assets = this.productStateManager.assets();
    const loading = this.productStateManager.assetsLoading();
    const error = this.productStateManager.assetsError();
    return {
      response: assets,
      loadingState: loading ? LoadingState.Loading : (error ? LoadingState.Error : LoadingState.Loaded)
    };
  });

  readonly productSpecifications = computed(() => {
    const product = this._product();
    const specifications = product?.specifications || [];
    return {
      loadingState: LoadingState.Loaded,
      response: specifications
    };
  });

  readonly productShortDescription = computed(() => {
    const product = this._product();
    return product?.short_description || '';
  });

  readonly productsInABoxState = computed(() => {
    const productsInBox = this.productStateManager.productsInABox();
    return productsInBox;
  });

  readonly productsInABoxStateWrapper = computed(() => {
    const productsInBox = this.productStateManager.productsInABox();
    const loading = this.productStateManager.productsInABoxLoading();
    const error = this.productStateManager.productsInABoxError();
    return {
      response: { products_in_box: productsInBox },
      loadingState: loading ? LoadingState.Loading : (error ? LoadingState.Error : LoadingState.Loaded)
    };
  });

  // Computed signal to check template condition
  readonly productsInABoxCondition = computed(() => {
    const productsInBox = this.productsInABoxState();
    return productsInBox?.length || 0;
  });

  // Computed signal for the product to use in template
  readonly productData = computed(() => {
    return this._product();
  });



  readonly errorMessage = input<string>('');

  // Update outputs to use Angular 20 output() function
  addToCart$ = output<CartData>();
  buyNow$ = output<Product>();
  refetchProduct$ = output<true>();

  // Access ProductStateManager for signal-based product data
  get productStateManager(): ProductStateManager {
    return this.productsRepositoryService.getProductStateManager();
  }

  // Private signals for internal state
  private _isDealAvailable = signal<boolean>(false);
  private _productDealCountDown = signal<ProductDealCountdown | undefined>(undefined);
  private _endDay = signal<Date>(new Date());
  private _rzpKey = signal<string>('');
  private razorpayInitialized = false;

  // Computed signals for derived state
  readonly isDealAvailable = this._isDealAvailable.asReadonly();
  readonly productDealCountDown = this._productDealCountDown.asReadonly();
  readonly endDay = this._endDay.asReadonly();
  readonly rzpKey = this._rzpKey.asReadonly();

  constructor() {
    this.isBrowser = this.platformService.isBrowser();

    // Initialize static data in constructor
    this._announcement.set(this.announcementsService.getAnnouncement(AnnouncementBarPage.ProductDetailPage));
    this._badges.set(this.announcementsService.getBadges());

    effect(() => {
      const productValue = this.productInput();
      if (productValue) {
        this._product.set(productValue);
        this._product.update(current => ({
          ...current,
          images: mapImageUrl(productValue?.featured_images || []).filter((url): url is string => url !== undefined)
        }));
        const { actualPrice, sellingPrice } = this.priceService.getProductPrice(productValue);
        this._sellingPrice.set(sellingPrice);
        this._actualPrice.set(actualPrice);
        this._isDealAvailable.set(!!productValue?.deal);
        this._productDealCountDown.set(productValue?.deal);
        this._availableOffers.set(productValue.price_list.other_offers);
      }
    });

    // Set up effects for reactive updates
    effect(() => {
      const storeConfig = this.storeConfigurationRepositoryService.storeConfigurations();
      if (storeConfig) {
        this._storeConfiguration.set(storeConfig);
        this._showGptWidgets.set(storeConfig?.show_gpt_widget ?? false);

        // Handle Razorpay initialization when store config changes
        const rzpKey = this.storeConfigurationRepositoryService.razorpayConfiguration()?.merchant_id || '';
        this._rzpKey.set(rzpKey);

        if (this.isBrowser && rzpKey && !this.razorpayInitialized) {
          this.initializeRazorpay(rzpKey);
        }
      }
    });

    effect(() => {
      const pageMask = this.cartRepositoryService.pageMasking();
      this._pageMask.set(pageMask);
    });

    effect(() => {
      const remainingTime = this.dealContDown.remainingTime();
      this._remainingTime.set(remainingTime);
    });

    // Effect for product changes
    effect(() => {
      const product = this._product();
      if (product) {
        this.productAttributeSelectionsService.prepareAttributeForm(product);
        this._currentAttributeSelections.set(this.productAttributeSelectionsService.attributeSelections);
      }
    });

    // Effect for deal countdown
    effect(() => {
      const isDealAvailable = this._isDealAvailable();
      const productDealCountDown = this._productDealCountDown();

      if (isDealAvailable && this.isBrowser && productDealCountDown) {
        this._endDay.set(new Date(productDealCountDown.end_time));
        this.dealContDown.getCountDown(this._endDay());
      }
    });
  }



  onRefetchProduct() {
    this.refetchProduct$.emit(true);
  }

  scrollToSection(sectionId: ScrollAbleSectionIds) {
    const section = this.scrollAbleSections.filter(_section => _section.nativeElement.id === `${sectionId}`)[0];
    section?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  addToCart({ product, bid_price }: { product: Product; bid_price: number }) {
    const form = this.currentAttributeSelections();
    form.markAllAsTouched();
    if (form.valid) {
      this.addToCart$.emit({ product, bid_price, productDetailOverlay: false, stayOnCurrentPage: false });
    } else {
      const requiredKey = this.getFormErrors(form);
      if (requiredKey) {
        const requiredAttribute = Object.keys(requiredKey)?.[0];
        this.notificationService.showInfo(`Please select ${requiredAttribute} to add it to the cart`);
      }
    }
  }

  buyNow(product: Product) {
    const form = this.currentAttributeSelections();
    form.markAllAsTouched();
    if (form.valid) {
      this.buyNow$.emit(product);
    } else {
      const requiredKey = this.getFormErrors(form);
      if (requiredKey) {
        const requiredAttribute = Object.keys(requiredKey)?.[0];
        this.notificationService.showInfo(`Please select ${requiredAttribute} to add it to the cart`);
      }
    }
  }

  private getFormErrors(form: AbstractControl) {
    if (form instanceof FormControl) {
      return form.errors ?? null;
    }
    if (form instanceof FormGroup) {
      const groupErrors = form.errors;
      const formErrors: { [key: string]: any } = groupErrors ? { groupErrors } : {};
      Object.keys(form.controls).forEach(key => {
        const error = this.getFormErrors(form.get(key)!);
        if (error !== null) {
          formErrors[key] = error;
        }
      });
      return Object.keys(formErrors).length > 0 ? formErrors : null;
    }
  }

  openImagesInFullScreen(previewIndex: number, product: Product) {
    const productName = product?.display_name || product?.name;
    this.fullScreenService.openInFullScreen<ImageCarouselFullScreenComponent>
      (this.viewContainerRef, this.viewContainerRef, ImageCarouselFullScreenComponent, productName, {
        imagesListInput: product?.images,
        previewIndex: previewIndex
      });
  }

  openImagesInFullScreenInMobile(previewIndex: number, product: Product) {
    this.mobileFullScreenService.openInFullScreen<MobileImageCarouselFullScreenComponent>
      (this.viewContainerRef, this.viewContainerRef, MobileImageCarouselFullScreenComponent, {
        imagesListInput: product?.images,
        previewIndex: previewIndex
      });
  }

  openExchangePolicy() {
    this.overlayService.open(ShippingPolicyComponent, null, true, 'centered');
  }

  private initializeRazorpay(rzpKey: string): void {
    this.loadRazorpayScript().then(() => {
      const RazorpayAffordabilitySuite = (window as any).RazorpayAffordabilitySuite;
      if (RazorpayAffordabilitySuite && rzpKey) {
        const amount = (+this._sellingPrice().toFixed(2)) * 100;
        const widgetConfig = {
          key: rzpKey,
          amount
        };
        const rzpAffordabilitySuite = new RazorpayAffordabilitySuite(widgetConfig);
        rzpAffordabilitySuite.render();
        this.razorpayInitialized = true;
      }
    }).catch(error => {
      // Script loading failed
    });
  }

  private loadRazorpayScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).RazorpayAffordabilitySuite) {
        resolve();
        return;
      }
      const script = this.document.createElement('script');
      script.src = 'https://cdn.razorpay.com/widgets/affordability/affordability.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Razorpay script'));
      this.document.body.appendChild(script);
    });
  }
}

export function mapImageUrl(featured_images: Image[] | undefined): (string | undefined)[] {
  if (!Array.isArray(featured_images)) {
    return [];
  }
  return featured_images.map(image => image.url);
}


